/**
 * Homepage mobile helpers — loaded only on the index template (≤ 767 px).
 *
 * Featured-collection + products-bundle carousels (2 cards visible + peek third, swipe + arrows)
 * ---------------------------------------------------------------------------
 * Splide JS and native scroll fight over layout. We apply inline styles with
 * `setProperty(..., 'important')` after Splide mounts, disable Splide drag, and
 * retry on lazy tab / carousel injection.
 */
(function () {
  if (!window.matchMedia('(max-width: 767px)').matches) return;

  var SECTION_SELECTORS = [
    '[id*="e32885d8"]',
    '[id*="featured_collection_hiHaQJ"]',
    '[id*="products_bundle_6P93z8"]',
    '[id*="collection_list_carousel_LUFhkj"]',
    '[id*="collage_D4GGTK"]'
  ];

  var CAROUSEL_PADDING = 20; /* 1.25rem left gutter only — right edge peeks */
  var PEEK_DIVISOR = 2.22; /* 2 full cards + ~22% peek of third */

  function imp(el, prop, val) {
    el.style.setProperty(prop, val, 'important');
  }

  var CAROUSEL_PROPS = [
    'overflow-x', 'overflow-y', 'scroll-snap-type', '-webkit-overflow-scrolling',
    'scrollbar-width', 'width', 'display', 'flex-wrap', 'gap', 'transform',
    'height', 'padding', 'margin', 'max-width', 'min-width', 'flex-shrink',
    'flex', 'margin-right', 'box-sizing', 'overflow', 'scroll-snap-align',
    'scroll-snap-stop', 'touch-action'
  ];

  function resetCardImageSplides(section) {
    section.querySelectorAll('.card-product .splide__track, .card-product .splide__list, .card-product .splide__slide').forEach(function (el) {
      CAROUSEL_PROPS.forEach(function (prop) {
        el.style.removeProperty(prop);
      });
    });
  }

  function getCarouselTracks(section) {
    return Array.from(section.querySelectorAll(
      '.collection > .splide__track, .x-block-products-list > .splide__track, .collection-list-wrapper > .splide__track, [id^="collage-"].splide > .splide__track'
    )).filter(function (track) {
      return !track.closest('.card-product');
    });
  }

  function disableSplideDrag(carousel) {
    if (!carousel || carousel._splideDragDisabled) return;
    var splide = carousel.splide;
    if (!splide) return;

    try {
      splide.options.drag = false;
      if (splide.Components && splide.Components.Drag) {
        splide.Components.Drag.disable(true);
      }
    } catch (e) { /* Splide API may vary */ }

    carousel._splideDragDisabled = true;
  }

  /* Let vertical swipes scroll the page; only lock horizontal when intent is clearly sideways */
  function wireGestureAxisLock(track) {
    if (track._gestureAxisWired) return;
    track._gestureAxisWired = true;

    var startX = 0;
    var startY = 0;
    var axis = null;
    var THRESHOLD = 10;

    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      axis = null;
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;

      if (!axis && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }

      if (axis === 'y') {
        track.style.setProperty('overflow-x', 'hidden', 'important');
      }
    }, { passive: true });

    function resetAxis() {
      axis = null;
      imp(track, 'overflow-x', 'auto');
    }

    track.addEventListener('touchend', resetAxis, { passive: true });
    track.addEventListener('touchcancel', resetAxis, { passive: true });
  }

  function getSlideWidth(trackW) {
    var gap = 12;
    var viewportW = document.documentElement.clientWidth || window.innerWidth;
    var basis = trackW > 0 ? trackW : Math.max(0, viewportW - CAROUSEL_PADDING);
    return Math.max(120, Math.floor((basis - gap) / PEEK_DIVISOR));
  }

  function setupCarousel(section) {
    if (!section) return false;

    resetCardImageSplides(section);

    var tracks = getCarouselTracks(section);
    if (!tracks.length) return false;

    var anyReady = false;

    tracks.forEach(function (track) {
      var list = track.querySelector('.splide__list');
      if (!list) return;

      var trackW = track.clientWidth;
      if (trackW <= 0) return;

      var gap = 12;
      var slideW = getSlideWidth(trackW);
      var slides = Array.from(list.querySelectorAll(
        ':scope > .splide__slide, :scope > [class*="preload-slide-mobile--"], :scope > [class*="preload-slide--"], :scope > [class*="bundle-splide-slide-"], :scope > [class*="collage-splide-slide-"]'
      ));
      if (!slides.length) return;

      var carousel = track.closest('.collection, .x-block-products-list, .collection-list-wrapper, [id^="collage-"].splide');
      disableSplideDrag(carousel);

      imp(track, 'overflow-x', 'auto');
      imp(track, 'overflow-y', 'hidden');
      imp(track, 'scroll-snap-type', 'x mandatory');
      imp(track, '-webkit-overflow-scrolling', 'touch');
      imp(track, 'scrollbar-width', 'none');
      imp(track, 'touch-action', 'manipulation');
      imp(track, 'width', '100%');

      if (!track._scrollbarStyleAdded) {
        var host = track.closest('[id]');
        if (host && host.id) {
          var st = document.createElement('style');
          st.textContent =
            '#' + host.id + ' .collection > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' .x-block-products-list > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' .collection-list-wrapper > .splide__track::-webkit-scrollbar{display:none}' +
            '#' + host.id + ' > .splide__track::-webkit-scrollbar{display:none}';
          document.head.appendChild(st);
          track._scrollbarStyleAdded = true;
        }
      }

      imp(list, 'display', 'flex');
      imp(list, 'flex-wrap', 'nowrap');
      imp(list, 'gap', gap + 'px');
      imp(list, 'transform', 'none');
      imp(list, 'width', 'auto');
      imp(list, 'height', 'auto');
      imp(list, 'padding', '0');
      imp(list, 'margin', '0');

      slides.forEach(function (slide) {
        imp(slide, 'width', slideW + 'px');
        imp(slide, 'max-width', slideW + 'px');
        imp(slide, 'min-width', slideW + 'px');
        imp(slide, 'flex', '0 0 ' + slideW + 'px');
        imp(slide, 'flex-shrink', '0');
        imp(slide, 'margin-right', '0');
        imp(slide, 'box-sizing', 'border-box');
        imp(slide, 'overflow', 'hidden');
        imp(slide, 'scroll-snap-align', 'start');
        slide.style.removeProperty('scroll-snap-stop');
      });

      var scrollStep = slideW + gap;

      var arrowHost = carousel || section;
      var prevBtn = arrowHost.querySelector('.splide__arrow--prev');
      var nextBtn = arrowHost.querySelector('.splide__arrow--next');

      if (prevBtn && !prevBtn._mobileWired) {
        prevBtn._mobileWired = true;
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
        });
      }
      if (nextBtn && !nextBtn._mobileWired) {
        nextBtn._mobileWired = true;
        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          track.scrollBy({ left: scrollStep, behavior: 'smooth' });
        });
      }

      function refreshArrows() {
        var atStart = track.scrollLeft <= 4;
        var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
        if (prevBtn) {
          prevBtn.disabled = atStart;
          prevBtn.style.opacity = atStart ? '0.3' : '';
        }
        if (nextBtn) {
          nextBtn.disabled = atEnd;
          nextBtn.style.opacity = atEnd ? '0.3' : '';
        }
      }

      if (!track._mobileScrollWired) {
        track._mobileScrollWired = true;
        track.addEventListener('scroll', refreshArrows, { passive: true });
      }
      wireGestureAxisLock(track);
      refreshArrows();

      track._mobileCarouselReady = true;
      anyReady = true;
    });

    return anyReady;
  }

  function setupAll() {
    var allOk = true;
    SECTION_SELECTORS.forEach(function (sel) {
      var section = document.querySelector(sel);
      if (!section) return;
      if (!setupCarousel(section)) allOk = false;
    });
    return allOk;
  }

  function trySetup(attemptsLeft) {
    var ok = setupAll();
    if (!ok && attemptsLeft > 0) {
      var delay = attemptsLeft === 3 ? 300 : attemptsLeft === 2 ? 800 : 1600;
      setTimeout(function () { trySetup(attemptsLeft - 1); }, delay);
    }
  }

  function resetSectionCarousels(section) {
    if (!section) return;
    getCarouselTracks(section).forEach(function (track) {
      track._mobileCarouselReady = false;
    });
    section.querySelectorAll('.collection, .x-block-products-list, .collection-list-wrapper, [id^="collage-"].splide').forEach(function (carousel) {
      carousel._splideDragDisabled = false;
    });
    setupCarousel(section);
  }

  function init() {
    trySetup(3);

    SECTION_SELECTORS.forEach(function (sel) {
      var section = document.querySelector(sel);
      if (!section) return;

      var sectionId = section.id.replace('shopify-section-', '');
      document.addEventListener('eurus:' + sectionId + ':splide-ready', function () {
        resetSectionCarousels(section);
      });

      var debounceTimer;
      var observer = new MutationObserver(function (mutations) {
        if (!window.matchMedia('(max-width: 767px)').matches) return;
        /* Ignore badge/label DOM churn — it was re-triggering carousel resets and stacking badges */
        var relevant = mutations.some(function (m) {
          var nodes = [];
          m.addedNodes.forEach(function (n) { nodes.push(n); });
          m.removedNodes.forEach(function (n) { nodes.push(n); });
          return nodes.some(function (n) {
            if (!n || n.nodeType !== 1) return false;
            if (n.classList && (
              n.classList.contains('x-badge-container')
              || n.classList.contains('label-container')
              || n.classList.contains('custom-label-container')
            )) return false;
            if (n.closest && n.closest('.label-container, .custom-label-container, .x-badge-container')) return false;
            return true;
          });
        });
        if (!relevant) return;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function () { resetSectionCarousels(section); }, 150);
      });
      observer.observe(section, { childList: true, subtree: true });
    });

    window.addEventListener('resize', function () {
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      setupAll();
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Video looping — mobile cover-flow touch scroll (layout handled by video-looping.js) */
  var VIDEO_LOOPING_SECTION = '[id*="video_looping_ijQzex"]';

  function initVideoLoopingCoverFlow() {
    var section = document.querySelector(VIDEO_LOOPING_SECTION);
    if (!section) return;

    var carousel = section.querySelector('.video-looping-mobile-cover-flow');
    if (carousel) {
      var slider = carousel.querySelector('[class*="slider-template"]');
      if (slider && !slider._coverFlowTouchWired) {
        slider._coverFlowTouchWired = true;
        imp(slider, 'touch-action', 'pan-y');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoLoopingCoverFlow);
  } else {
    initVideoLoopingCoverFlow();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.matches && e.target.matches(VIDEO_LOOPING_SECTION)) {
      initVideoLoopingCoverFlow();
    }
  });
})();
